import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation } from '@apollo/client';

import AddButtonSVG from './assets/add-button.svg?react';
import Spinner from '../../assets/spinner.svg?react';
import { SubmitAssignmentSimpleDocument } from '../../services/graphql/types/graphql';
import { SUBMIT_ASSIGNMENT_SIMPLE_DRAFT } from '../../services/graphql/draftAssignment.operations';
import { toast } from 'react-toastify';
import ImageBlobReducer from 'image-blob-reduce';
import { isAppOrX5WebView } from '../../utils/webview';

const X5_CAMERA_HINT =
  '当前环境无法直接打开相机。请先用系统相机拍照，再点击「从相册选择」上传该照片。';

const CAMERA_RESULT_CB = '__MUYI_CAMERA_RESULT__';
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

const isVideoFile = (file: File) => file.type.startsWith('video/');

const ArtworkUpload = ({
  assignmentId,
  refetchQueries = ['GetHomePageData'],
  onFinish,
  draftMode = false,
}: {
  assignmentId: string;
  refetchQueries?: string[];
  onFinish?: () => void;
  draftMode?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const albumInputRef = useRef<HTMLInputElement>(null);
  const [showWebViewMenu, setShowWebViewMenu] = useState(false);
  const useDualEntry = isAppOrX5WebView();

  const openCamera = () => {
    setShowWebViewMenu(false);
    const win = window as any;
    if (win.messageHandlers?.bridge) {
      const callbackId = 'camera_' + Date.now();
      win[CAMERA_RESULT_CB] = (cid: string, base64: string | null) => {
        if (cid !== callbackId) return;
        win[CAMERA_RESULT_CB] = null;
        if (!base64) {
          toast.info('已取消拍照');
          return;
        }
        const dataUrl = 'data:image/jpeg;base64,' + base64;
        fetch(dataUrl)
          .then((r) => r.blob())
          .then((blob) => submitImageFile(blob, 'camera.jpg'))
          .catch(() => toast.error('图片处理失败'));
      };
      win.messageHandlers.bridge(
        JSON.stringify({ type: 'openCamera', callbackId }),
      );
    } else {
      toast.info(X5_CAMERA_HINT, { autoClose: 6000 });
    }
  };

  const openAlbum = () => {
    setShowWebViewMenu(false);
    albumInputRef.current?.click();
  };

  const [submitAssignmentSimple, { data, loading, error }] = useMutation(
    draftMode ? SUBMIT_ASSIGNMENT_SIMPLE_DRAFT : SubmitAssignmentSimpleDocument,
    {
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const buildUploadVariables = (file: File | Blob, fileName = 'camera.jpg') => {
    const uploadFile =
      file instanceof File ? file : new File([file], fileName, { type: 'image/jpeg' });
    return {
      file: {
        file: uploadFile,
        assignmentId,
        ...(draftMode ? { draftMode: true } : {}),
      },
    };
  };

  const logSubmission = (submission: NonNullable<typeof data>['submitAssignmentSimple']) => {
    const medias =
      submission?.artworks[0]?.medias.toSorted((a, b) => (a.priority ?? 0) - (b.priority ?? 0)) ??
      [];
    console.info(
      `Submission ${submission?.submissionId} artwork media uploaded. MediaId: ${
        medias[medias.length - 1]?.mediaId
      }. Url ${medias[medias.length - 1]?.url}`,
    );
  };

  const submitVideoFile = async (videoFile: File) => {
    if (videoFile.size > MAX_VIDEO_SIZE) {
      toast.error('视频大小不能超过50MB');
      return;
    }
    const { data } = await submitAssignmentSimple({
      variables: buildUploadVariables(videoFile),
    });
    if (data?.submitAssignmentSimple) {
      logSubmission(data.submitAssignmentSimple);
    }
  };

  const submitImageFile = async (imageFile: File | Blob, fileName = 'camera.jpg') => {
    const file = imageFile instanceof File ? imageFile : new File([imageFile], fileName, { type: 'image/jpeg' });
    const imageReducer = new ImageBlobReducer();
    let resizedImage: Blob;
    try {
      resizedImage = await imageReducer.toBlob(file, { max: 3000 });
    } catch (error) {
      toast.error('不支持的图片格式');
      return;
    }
    const { data } = await submitAssignmentSimple({
      variables: buildUploadVariables(resizedImage, file.name),
    });
    if (data?.submitAssignmentSimple) {
      logSubmission(data.submitAssignmentSimple);
    }
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    setShowWebViewMenu(false);
    const { files, value } = e.target;
    if (!value) return;

    const selectedFile = files?.[0];
    if (!selectedFile) {
      console.error('No file selected.');
      toast.error('请选择图片或视频');
      return;
    }
    console.info(
      `Select file: ${selectedFile.name}. Size: ${selectedFile.size} Bytes. Type: ${selectedFile.type}`,
    );

    if (isVideoFile(selectedFile)) {
      await submitVideoFile(selectedFile);
    } else {
      await submitImageFile(selectedFile);
    }
    e.target.value = '';
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (useDualEntry) {
      const win = window as any;
      if (win.messageHandlers?.bridge) {
        setShowWebViewMenu((v) => !v);
        return;
      }
      albumInputRef.current?.click();
      return;
    }
    inputRef.current?.click();
  };

  return (
    <div className="overflow-hidden bg-[#bcbcbc] h-full">
      {loading ? (
        <div className="loading-icon">
          <Spinner className="scale-50"></Spinner>
        </div>
      ) : (
        <>
          <div className="text-[150%] w-full h-full flex items-center justify-center relative z-[100]">
            <div className="w-[36px] h-[36px] cursor-pointer">
              <AddButtonSVG onClick={handleAddClick} />
            </div>
          </div>
          {useDualEntry &&
            showWebViewMenu &&
            createPortal(
              <div
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4"
                onClick={() => setShowWebViewMenu(false)}
                role="dialog"
                aria-modal="true"
                aria-label="选择上传方式"
              >
                <div
                  className="bg-white rounded-xl shadow-lg w-full max-w-[280px] overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-3.5 text-base border-b border-gray-100 active:bg-gray-50"
                    onClick={openCamera}
                  >
                    拍照
                  </button>
                  <button
                    type="button"
                    className="block w-full text-left px-4 py-3.5 text-base active:bg-gray-50"
                    onClick={openAlbum}
                  >
                    从相册选择
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </>
      )}
      {useDualEntry ? (
        <>
          <input
            ref={cameraInputRef}
            className="absolute opacity-0 w-0 h-0 top-0 left-0"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onChange}
            aria-label="camera"
          />
          <input
            ref={albumInputRef}
            className="absolute opacity-0 w-0 h-0 top-0 left-0"
            type="file"
            accept="image/*,video/*"
            onChange={onChange}
            aria-label="album"
          />
        </>
      ) : (
        <input
          ref={inputRef}
          className="absolute opacity-0 w-0 h-0 top-0 left-0"
          type="file"
          accept="image/*,video/*"
          required
          onChange={onChange}
          aria-label="upload"
        />
      )}
    </div>
  );
};

export default ArtworkUpload;
